import { useState } from 'react';
import { FamilyMember } from '@/types';
import { useFamilyStore } from '@/store/familyStore';
import { useAuthStore } from '@/store/authStore';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AddChildForm } from './forms/AddChildForm';
import { DeleteConfirmation } from './forms/DeleteConfirmation';
import { EditNameForm } from './forms/EditNameForm';

interface TreeNodeDialogProps {
  member: FamilyMember;
  onClose: () => void;
  mode?: 'view' | 'edit' | 'delete' | 'add-child';
}

export function TreeNodeDialog({ member, onClose, mode = 'view' }: TreeNodeDialogProps) {
  const { updateMember, deleteMember, addMember } = useFamilyStore();
  const user = useAuthStore((state) => state.user);
  const isAdmin = user?.role === 'admin';

  const handleAddChildren = (names: string[]) => {
    if (!isAdmin) return;

    const newMembers = names.map(name => ({
      id: crypto.randomUUID().replace(/[-]/g, ""),
      name: name.trim(),
      parentId: member.id,
      children: []
    }));

    // Add all new members
    newMembers.forEach(newMember => {
      addMember(newMember);
    });

    // Update parent's children array
    updateMember(member.id, {
      children: [...member.children, ...newMembers.map(m => m.id)]
    });

    onClose();
  };

  const handleUpdateName = (newName: string) => {
    if (!newName.trim() || !isAdmin) return;
    updateMember(member.id, { name: newName.trim() });
    onClose();
  };

  const handleDelete = () => {
    if (!isAdmin) return;
    deleteMember(member.id);
    onClose();
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {mode === 'add-child' ? 'إضافة مولود جديد' :
             mode === 'delete' ? 'تأكيد الحذف' :
             mode === 'edit' ? 'تعديل الاسم' :
             member.name}
          </DialogTitle>
        </DialogHeader>

        {mode === 'add-child' ? (
          <AddChildForm onSubmit={handleAddChildren} onCancel={onClose} />
        ) : mode === 'delete' ? (
          <DeleteConfirmation 
            memberName={member.name} 
            onConfirm={handleDelete} 
            onCancel={onClose} 
          />
        ) : mode === 'edit' ? (
          <EditNameForm 
            initialName={member.name} 
            onSubmit={handleUpdateName} 
            onCancel={onClose} 
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}